import { useState, useEffect } from "react";
import axios from "axios";
import {
  TextField,
  Button,
  Typography,
  Alert,
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Chip,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import PublishIcon from "@mui/icons-material/Publish";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3001";

// 白色主题卡片，无hover效果
const GlassCard = styled(Paper)(({ theme }) => ({
  background: "rgba(0, 0, 0, 0.25)",
  backdropFilter: "blur(15px)",
  WebkitBackdropFilter: "blur(15px)",
  borderRadius: "24px",
  padding: theme.spacing(4),
  border: "1px solid rgba(255, 255, 255, 0.2)",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  position: "relative",
  color: "#fff",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(255, 255, 255, 0.05)",
    borderRadius: 24,
    pointerEvents: "none",
  },
  // 移除hover效果
}));

// 白色主题输入框及helperText
const GlassTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    background: "rgba(255,255,255,0.1)",
    borderRadius: "12px",
    "& fieldset": {
      borderColor: "rgba(255, 255, 255, 0.2)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(255, 255, 255, 0.3)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "rgba(255, 255, 255, 0.4)",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#fff",
    fontSize: "0.9rem",
    "&.Mui-focused": {
      color: "#fff",
    },
  },
  "& .MuiInputBase-input": {
    color: "#fff",
    fontSize: "0.95rem",
  },
  "& .MuiFormHelperText-root": {
    color: "#fff", // helperText 白色
    opacity: 0.85,
  },
  marginBottom: theme.spacing(3),
}));

// 白色主题按钮，无hover和禁用文字特殊色
const GlassButton = styled(Button)(({ theme, color = "#fff" }) => ({
  background: `linear-gradient(135deg, ${color}15, ${color}05)`,
  backdropFilter: "blur(6px)",
  WebkitBackdropFilter: "blur(6px)",
  borderRadius: "8px",
  color: "#fff",
  padding: theme.spacing(1, 3),
  border: `1px solid ${color}20`,
  transition: "all 0.3s ease",
  // 移除hover效果
  "&:hover": {
    background: `linear-gradient(135deg, ${color}15, ${color}05)`,
    transform: "none",
    boxShadow: "none",
    color: "#fff",
  },
  // 禁用时文字仍为白色
  "&:disabled": {
    background: "rgba(255, 255, 255, 0.14)",
    color: "#fff",
    opacity: 0.5,
  },
}));

// 白色主题表格，透明背景，无hover效果
const GlassTable = styled(Table)(({ theme }) => ({
  "& .MuiTableCell-root": {
    borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
    background: "transparent",
    color: "#fff",
    fontSize: "0.95rem",
    padding: theme.spacing(2),
  },
  "& .MuiTableHead-root .MuiTableCell-root": {
    background: "transparent",
    fontWeight: 600,
    fontSize: "1rem",
    color: "#fff",
  },
  // 移除表格hover变化
}));

// 毛玻璃对话框（内容改为白色）
const GlassDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    background: "rgba(50, 50, 50, 0.96)",
    backdropFilter: "blur(15px)",
    WebkitBackdropFilter: "blur(15px)",
    borderRadius: "16px",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
    color: "#fff",
  },
}));

const DailyEventManagement = () => {
  const [devotionals, setDevotionals] = useState([]);
  const [bibleVerse, setBibleVerse] = useState(""); // 经文
  const [content, setContent] = useState(""); // 灵修内容
  const [date, setDate] = useState(""); // 日期
  const [type, setType] = useState("devotional"); // 类型：devotional(灵修) 或 gospel(福音)
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [deployLoading, setDeployLoading] = useState(false);
  const [deploySuccess, setDeploySuccess] = useState("");

  // 获取所有灵修/福音内容
  useEffect(() => {
    fetchDevotionals();
  }, []);

  const fetchDevotionals = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/devotionals`);
      setDevotionals(response.data);
    } catch (err) {
      console.error("获取灵修内容失败:", err);
      setError("获取列表失败");
    } finally {
      setLoading(false);
    }
  };

  // 重置表单
  const resetForm = () => {
    setBibleVerse("");
    setContent("");
    setDate("");
    setType("devotional");
    setCurrentId(null);
    setIsEditing(false);
    setError("");
  };

  // 处理表单提交
  const handleSubmit = async (e) => {
    e.preventDefault();

    // 验证必填字段
    if (!bibleVerse.trim()) {
      setError("经文不能为空");
      return;
    }
    if (!content.trim()) {
      setError("内容不能为空");
      return;
    }
    if (!date) {
      setError("日期不能为空");
      return;
    }

    const devotionalData = {
      bibleVerse,
      content,
      date: new Date(date).toISOString(),
      type,
    };

    try {
      setLoading(true);
      if (isEditing) {
        await axios.put(
          `${API_BASE_URL}/devotionals/${currentId}`,
          devotionalData
        );
        setSuccess("更新成功！");
      } else {
        await axios.post(`${API_BASE_URL}/devotionals`, devotionalData);
        setSuccess("创建成功！");
      }
      resetForm();
      fetchDevotionals();
    } catch (err) {
      console.error("保存失败:", err);
      setError(isEditing ? "更新失败" : "创建失败");
    } finally {
      setLoading(false);
    }
  };

  // 处理编辑
  const handleEdit = async (id) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/devotionals/${id}`);
      const devotional = response.data;

      setBibleVerse(devotional.bibleVerse);
      setContent(devotional.content);
      setDate(new Date(devotional.date).toISOString().slice(0, 10));
      setType(devotional.type);

      setCurrentId(id);
      setIsEditing(true);
    } catch (err) {
      console.error("获取详情失败:", err);
      setError("获取详情失败");
    } finally {
      setLoading(false);
    }
  };

  // 处理删除
  const handleDelete = async (id) => {
    if (!window.confirm("确定要删除这条内容吗？")) {
      return;
    }

    try {
      setLoading(true);
      console.log("正在删除内容，ID:", id);
      const response = await axios.delete(`${API_BASE_URL}/devotionals/${id}`);
      console.log("删除响应:", response);

      if (response.data && response.data.message) {
        setSuccess(response.data.message);
        console.log("开始重新获取列表");
        await fetchDevotionals(); // 重新获取列表
        console.log("列表获取完成");
      }
    } catch (err) {
      console.error("删除失败:", err);
      const errorMessage =
        err.response?.data?.message || "删除失败，请稍后重试";
      console.error("错误信息:", errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // 部署到首页
  const handleDeploy = async (item) => {
    try {
      setDeployLoading(true);

      // 处理日期，使用美国西部时间
      const localDate = new Date(item.date);
      const pstDate = new Date(
        localDate.toLocaleString("en-US", {
          timeZone: "America/Los_Angeles",
        })
      );

      // 更新devotional内容
      const devotionalData = {
        bibleVerse: item.bibleVerse,
        content: item.content,
        date: pstDate.toISOString(),
        type: item.type,
      };

      // 调用devotionals API进行更新
      await axios.put(
        `${API_BASE_URL}/devotionals/${item._id}`,
        devotionalData
      );

      setDeploySuccess(
        `已成功更新${item.type === "devotional" ? "灵修" : "福音"}内容`
      );
      setTimeout(() => setDeploySuccess(""), 3000);

      // 刷新列表
      await fetchDevotionals();
    } catch (error) {
      console.error("部署失败:", error);
      setError("部署失败，请稍后重试");
    } finally {
      setDeployLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, width: "100%" }}>
      <Typography
        variant="h4"
        sx={{
          mb: 4,
          fontWeight: 500,
          color: "#fff",
          textShadow: "0 1px 2px rgba(0, 0, 0, 0.25)",
        }}
      >
        每日灵修/福音管理
      </Typography>

      {success && (
        <Alert
          severity="success"
          sx={{
            mb: 3,
            backgroundColor: "rgba(237, 247, 237, 0.6)",
            border: "1px solid rgba(46, 125, 50, 0.1)",
            borderRadius: "8px",
          }}
          onClose={() => setSuccess("")}
        >
          {success}
        </Alert>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            background: "rgba(244,67,54,0.18)",
            color: "#fff",
            border: "1px solid rgba(255,255,255,0.1)",
            "& .MuiAlert-icon": {
              color: "#fff",
            },
          }}
          onClose={() => setError("")}
        >
          {error}
        </Alert>
      )}

      {deploySuccess && (
        <Alert
          severity="success"
          sx={{
            mb: 2,
            backgroundColor: "rgba(237, 247, 237, 0.6)",
            border: "1px solid rgba(46, 125, 50, 0.1)",
            borderRadius: "8px",
          }}
        >
          {deploySuccess}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* 表单部分 */}
        <Grid item xs={12} md={4}>
          <GlassCard>
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Box
                sx={{ mb: 3, display: "flex", justifyContent: "space-between" }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 500,
                    color: "#fff",
                    textShadow: "0 1px 2px rgba(0, 0, 0, 0.18)",
                  }}
                >
                  {isEditing ? "编辑内容" : "创建内容"}
                </Typography>
                <Box>
                  <GlassButton
                    variant={type === "devotional" ? "contained" : "outlined"}
                    onClick={() => setType("devotional")}
                    sx={{ mr: 1 }}
                    color={type === "devotional" ? "#2196f3" : "#666666"}
                    size="small"
                  >
                    灵修
                  </GlassButton>
                  <GlassButton
                    variant={type === "gospel" ? "contained" : "outlined"}
                    onClick={() => setType("gospel")}
                    color={type === "gospel" ? "#4caf50" : "#666666"}
                    size="small"
                  >
                    福音
                  </GlassButton>
                </Box>
              </Box>

              <form onSubmit={handleSubmit}>
                <GlassTextField
                  fullWidth
                  label="经文"
                  value={bibleVerse}
                  onChange={(e) => setBibleVerse(e.target.value)}
                  required
                  helperText="例如：约翰福音 3:16"
                />

                <GlassTextField
                  fullWidth
                  label="内容"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  multiline
                  rows={6}
                  required
                />

                <GlassTextField
                  fullWidth
                  label="日期"
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                />

                <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                  {isEditing && (
                    <GlassButton
                      variant="outlined"
                      onClick={resetForm}
                      startIcon={<AddIcon sx={{ color: "#fff" }} />}
                      color="#4caf50"
                    >
                      新建
                    </GlassButton>
                  )}
                  <GlassButton
                    type="submit"
                    variant="contained"
                    startIcon={
                      isEditing ? (
                        <SaveIcon sx={{ color: "#fff" }} />
                      ) : (
                        <AddIcon sx={{ color: "#fff" }} />
                      )
                    }
                    disabled={loading}
                    color="#2196f3"
                    fullWidth
                  >
                    {isEditing ? "更新" : "创建"}
                  </GlassButton>
                </Box>
              </form>
            </Box>
          </GlassCard>
        </Grid>

        {/* 列表部分 */}
        <Grid item xs={12} md={8}>
          <GlassCard>
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  fontWeight: 500,
                  color: "#fff",
                  textShadow: "0 1px 2px rgba(0,0,0,0.18)",
                }}
              >
                内容列表
              </Typography>

              <TableContainer>
                <GlassTable>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: "#fff", fontSize: "1rem" }}>
                        日期
                      </TableCell>
                      <TableCell sx={{ color: "#fff", fontSize: "1rem" }}>
                        类型
                      </TableCell>
                      <TableCell sx={{ color: "#fff", fontSize: "1rem" }}>
                        经文
                      </TableCell>
                      <TableCell sx={{ color: "#fff", fontSize: "1rem" }}>
                        操作
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {devotionals.map((item) => (
                      <TableRow key={item._id}>
                        <TableCell sx={{ color: "#fff" }}>
                          {new Date(item.date).toLocaleDateString()}
                        </TableCell>
                        <TableCell sx={{ color: "#fff" }}>
                          <Chip
                            label={item.type === "devotional" ? "灵修" : "福音"}
                            color={
                              item.type === "devotional" ? "primary" : "success"
                            }
                            size="small"
                            sx={{
                              backgroundColor:
                                item.type === "devotional"
                                  ? "rgba(33, 150, 243, 0.13)"
                                  : "rgba(76, 175, 80, 0.13)",
                              color: "#fff",
                              border: `1px solid ${
                                item.type === "devotional"
                                  ? "#2196f3"
                                  : "#4caf50"
                              }40`,
                              fontWeight: 500,
                              letterSpacing: "1px",
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: "#fff" }}>
                          {item.bibleVerse}
                        </TableCell>
                        <TableCell sx={{ color: "#fff" }}>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Tooltip
                              title={
                                <span style={{ color: "#fff" }}>编辑</span>
                              }
                              componentsProps={{
                                tooltip: {
                                  sx: {
                                    color: "#fff",
                                    background: "rgba(0,0,0,.8)",
                                    fontWeight: 400,
                                  },
                                },
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(item._id)}
                                sx={{
                                  color: "#fff",
                                  position: "relative",
                                  zIndex: 1,
                                }}
                              >
                                <EditIcon
                                  fontSize="small"
                                  sx={{ color: "#fff" }}
                                />
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              title={
                                <span style={{ color: "#fff" }}>删除</span>
                              }
                              componentsProps={{
                                tooltip: {
                                  sx: {
                                    color: "#fff",
                                    background: "rgba(0,0,0,.8)",
                                    fontWeight: 400,
                                  },
                                },
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(item._id)}
                                sx={{
                                  color: "#fff",
                                  position: "relative",
                                  zIndex: 1,
                                }}
                              >
                                <DeleteIcon
                                  fontSize="small"
                                  sx={{ color: "#fff" }}
                                />
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              title={
                                <span style={{ color: "#fff" }}>
                                  部署到首页
                                  {item.type === "devotional" ? "灵修" : "福音"}
                                  卡片
                                </span>
                              }
                              componentsProps={{
                                tooltip: {
                                  sx: {
                                    color: "#fff",
                                    background: "rgba(0,0,0,.8)",
                                    fontWeight: 400,
                                  },
                                },
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() => handleDeploy(item)}
                                disabled={deployLoading}
                                sx={{
                                  color: "#fff",
                                  position: "relative",
                                  zIndex: 1,
                                }}
                              >
                                <PublishIcon
                                  fontSize="small"
                                  sx={{ color: "#fff" }}
                                />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </GlassTable>
              </TableContainer>
            </Box>
          </GlassCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DailyEventManagement;
